import { ButtonContent, PendingOverlay } from "@ui/components/internal/ButtonContent";
import { tv } from "@ui/lib/tv";
import { Button, type ButtonProps } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import { cx } from "@ui/utils/cx";
import type { ReactNode } from "react";

const promptButton = tv({
  base: [
    "relative inline-flex w-full cursor-pointer items-center gap-4 overflow-clip rounded-3xl bg-white",
    "h-12 p-4",
    "ring-1 ring-black/10",
    "lg:h-auto lg:px-7 lg:py-4",
    "transition-[background-color,box-shadow] duration-150 ease-in-out",
    "hover:bg-yellow-100 hover:ring-2 hover:ring-yellow-600",
    "pressed:bg-yellow-200 pressed:ring-2 pressed:ring-yellow-600",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "data-[pending]:cursor-default",
  ],
});

export type PromptButtonProps = ButtonProps & {
  /** Optional secondary text, hidden below md breakpoint (768px) */
  description?: ReactNode;
  /** Leading icon element, rendered in a 32px container */
  icon: ReactNode;
};

/**
 * Card-style button that prompts users to contribute content (celebrations, reviews).
 * Use instead of OutlineButton when the action needs a leading icon and optional description.
 */
export function PromptButton({ description, icon, ...props }: PromptButtonProps) {
  return (
    <Button
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        promptButton({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isHovered, isPending, isPressed }) => {
        const isHighlighted = isHovered || isPressed;

        return (
          <>
            {/* `min-w-0` keeps the inner truncation chain intact inside the flex shell. */}
            <ButtonContent className="min-w-0" isPending={isPending}>
              <span aria-hidden="true" className="inline-flex shrink-0 [&>*]:size-8">
                {icon}
              </span>
              <span className="flex min-w-0 items-baseline gap-2">
                <span
                  className={cx(
                    "font-semibold text-base leading-6 transition-colors duration-150 ease-in-out lg:text-lg",
                    isHighlighted ? "text-yellow-700" : "text-neutral-500",
                  )}
                >
                  {children}
                </span>
                {description && (
                  <span
                    className={cx(
                      "hidden text-sm italic leading-5 transition-colors duration-150 ease-in-out lg:inline",
                      isHighlighted ? "text-yellow-700" : "text-neutral-400",
                    )}
                  >
                    {description}
                  </span>
                )}
              </span>
            </ButtonContent>
            {/* White/yellow card fill — the white spinner needs inverting to stay visible. */}
            <PendingOverlay invert isPending={isPending} />
          </>
        );
      })}
    </Button>
  );
}
